/**
 * Module contract
 *
 * Owns:     how this app behaves under page-translation extensions (Google
 *           Translate, browser built-in translators, Grammarly-style injectors)
 * Produces: installTranslationDomGuard() — global, installed once at app entry;
 *           keeps React's commit phase from crashing.
 *           noTranslateProps() / markNoTranslate() — opt one subtree out of
 *           translation; applied per element.
 * Does NOT: keep translated text in sync with React state. The guard prevents
 *           the crash, nothing more.
 *
 * Why the guard is needed: a translation extension rewrites text nodes in
 *   place — a bare text node becomes `<font>text</font>`, so the node itself
 *   moves one level down. React still holds its original reference and calls
 *   `parent.insertBefore(newNode, staleRef)` or `parent.removeChild(stale)`;
 *   the browser throws `NotFoundError: ... is not a child of this node`, which
 *   unmounts the whole tree through the top-level ErrorBoundary.
 *
 * When to mark a surface: ask whose text it is and what language it is in. The
 *   student's own answer, key caps, captions, and anything whose geometry we
 *   measure must never be translated; slide content and interface prose must.
 *   Full rule with rationale lives in CLAUDE.md, "Page Translation" — one copy
 *   on purpose. Marked surfaces are not listed here either: `grep noTranslate
 *   src/` finds them all and cannot go stale.
 *
 * Portals: a portal renders into document.body and inherits nothing from its
 *   React-tree ancestors. Marking a container never covers what it portals out.
 *
 * Marking a container also swallows the little prose inside it. Accepted: those
 *   containers have their geometry measured, so moving prose to an unmarked
 *   sibling would risk cursor and pill positions to save an English string.
 *
 * Not covered: et-studio has the same three vulnerable sites and none are
 *   protected — it cannot import this module. Unifying them means moving this
 *   module into @ever-tutor/et-ui and releasing the package.
 */

const NO_TRANSLATE_CLASS = "notranslate";

const originalInsertBefore = Node.prototype.insertBefore;
const originalRemoveChild = Node.prototype.removeChild;
const originalAppendChild = Node.prototype.appendChild;

let wasInstalled = false;

// A global prototype patch must not be silent: every fallback it takes is an
// error the page would otherwise have raised, including genuine stale-reference
// bugs in app code or in reveal.js / tldraw / survey-core. Warn on the first few
// so the blast radius is measurable, then go quiet so a pathological page cannot
// flood the console. ErrorBoundary pipes console.warn into Datadog, so this
// needs no logger import — which matters, because this module loads before
// everything else and must stay dependency-free.
const MAX_FALLBACK_WARNINGS_PER_SESSION = 5;
let fallbackWarningCount = 0;

function warnFallbackTaken(branch: string, parent: Node): void {
  if (fallbackWarningCount >= MAX_FALLBACK_WARNINGS_PER_SESSION) return;
  fallbackWarningCount += 1;
  // This runs inside React's commit phase, and ErrorBoundary has replaced
  // console.warn with a reporter of its own. A guard whose entire job is "never
  // throw" must not start throwing because reporting failed.
  try {
    const parentName =
      (parent as Element).tagName?.toLowerCase() ?? parent.nodeName;
    console.warn(
      `[pageTranslation] DOM guard fallback: ${branch} (parent <${parentName}>). ` +
        "Expected under a page translator; unexpected otherwise.",
    );
  } catch {
    // Reporting is best-effort; the fallback itself still has to happen.
  }
}

export interface NoTranslateProps {
  translate: "no";
  className: string;
}

/**
 * Input: optional class names the element already needs
 * Output: props marking the element and its whole subtree as non-translatable
 *
 * IMPORTANT — this helper owns `className` at the call site. Pass the element's
 * classes through it; never write a separate className alongside the spread.
 * Both orderings fail silently and neither is a type error:
 *
 *     <div className="x" {...noTranslateProps()} />   // drops "x"
 *     <div {...noTranslateProps()} className="x" />   // drops "notranslate"
 *
 * The second is the dangerous one — the element looks marked and is not. When
 * an element already owns its className for other reasons, use markNoTranslate
 * through a ref instead of spreading.
 *
 * Both attribute and class are emitted because different mechanisms read them:
 * `translate="no"` is the HTML standard honoured by Chrome, Safari and Edge;
 * `notranslate` is Google's own legacy convention. The attribute is inherited
 * by descendants, so marking a container covers everything inside it.
 */
export function noTranslateProps(className?: string): NoTranslateProps {
  return {
    translate: "no",
    className: className
      ? `${className} ${NO_TRANSLATE_CLASS}`
      : NO_TRANSLATE_CLASS,
  };
}

/**
 * Input: an element built imperatively rather than through JSX
 * Output: the same element, for call-site chaining
 * Mutates: the element's `translate` attribute and class list
 *
 * Use where the DOM is constructed by hand — RevealRenderer's latexDisplay, the
 * SurveyJS adorners — and noTranslateProps has nothing to spread onto.
 */
export function markNoTranslate<T extends Element>(element: T): T {
  element.setAttribute("translate", "no");
  element.classList.add(NO_TRANSLATE_CLASS);
  return element;
}

/**
 * Input: the parent React is committing into, and the anchor it wants to insert
 *   before (which may have been reparented out from under it)
 * Output: the ancestor of `anchor` that is a direct child of `parent`, or null
 *   when `anchor` is not inside `parent` at all
 *
 * For a `<font>` wrapper this is one step up, which is what recovers the
 * intended insertion position rather than losing it.
 */
function resolveAnchorWithinParent(parent: Node, anchor: Node): Node | null {
  let node: Node | null = anchor;
  // The parentNode chain is finite and acyclic, so this always terminates.
  while (node && node.parentNode !== parent) {
    node = node.parentNode;
  }
  return node;
}

/**
 * Input: none
 * Output: none
 * Mutates: Node.prototype.insertBefore, Node.prototype.removeChild
 * Fired by: app entry point (main.jsx), before ReactDOM.createRoot().render()
 *
 * The normal path costs one extra `parentNode` comparison and then delegates to
 * the native implementation unchanged.
 */
export function installTranslationDomGuard(): void {
  if (wasInstalled) return;
  wasInstalled = true;

  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference sibling was reparented by a translation extension.
      const reanchored = resolveAnchorWithinParent(this, referenceNode);
      if (reanchored) {
        // Anchor is still inside this parent, just wrapped — insert at the
        // position React actually meant, so sibling order is preserved.
        return originalInsertBefore.call(this, newNode, reanchored) as T;
      }
      // Anchor left the tree entirely — the only branch where the intended
      // position is unrecoverable. Appending keeps the node in the tree so the
      // commit completes; appendChild moves a node, it never duplicates it.
      warnFallbackTaken("insertBefore anchor outside parent", this);
      return originalAppendChild.call(this, newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      // Bound the redirect to this subtree, exactly as the insertBefore path is
      // bounded by resolveAnchorWithinParent. Without this the guard would
      // happily delete a live node from an unrelated part of the document just
      // because some script asked the wrong parent to remove it. `contains`
      // also reports a node as containing itself, hence the identity check —
      // otherwise `n.removeChild(n)` would detach n from its own parent.
      if (child === this || !this.contains(child)) {
        warnFallbackTaken("removeChild target outside parent", this);
        return child;
      }
      // Inside this subtree but one level deeper — a wrapper moved it. Remove
      // it from where it actually lives. `contains` held and child is not
      // `this`, so parentNode is non-null.
      return originalRemoveChild.call(child.parentNode!, child) as T;
    }
    return originalRemoveChild.call(this, child) as T;
  };
}
